// A GameView fed by the server's snapshot stream instead of a locally-stepped Game. The player's OWN hero
// is predicted (net/predictedGame.ts): applySnapshot() overwrites its authoritative state (hp/alive/
// statuses — anything the server decides), while its pos/yaw/lift/state/swingAt/moving and cooldowns are
// driven by reconcile() below, called once a frame from net/onlineMatch.ts's render loop with the input
// buffer (net/commandChannel.ts) it needs to replay. Every OTHER hero, and every monster, is drawn a little
// in the past by advanceOthers() (net/entityInterp.ts), authoritative state and events included — see
// predictedGame.ts's own header on why they are never predicted.
//
// Four ViewHero objects are created once, at matchStart, and mutated in place by every snapshot — never
// replaced — so identity-keyed state elsewhere (heroView's rig lookup by id) stays stable exactly like
// offline's real Hero objects do.
import * as THREE from 'three';
import { RULES, STEP_DT, type HeroState } from '../../core/game.js';
import { HEROES, type HeroDef, type HeroKind } from '../../core/heroes.js';
import { LAYOUT, groundSpeed } from '../../core/layout.js';
import type { Team, VisionSource } from '../../core/layout.js';
import type { ArtifactSlotKey, Command, RelicLine, SkillKey } from '../../core/commands.js';
import { RELICS } from '../../core/relics.js';
import type { GameEvent, HeroId } from '../../core/events.js';
import type { MatchStats } from '../../core/stats.js';
import type { GameView, HeroStatus, HeroTelegraph, ViewBarrier, ViewTotem, ViewHero, ViewLob, ViewMonster, ViewPoint, ViewPool, ViewProjectile, ViewReveal, ViewSummon, ViewTeamBuffs, ViewTrap, YouStats } from '../gameView.js';
import { MONSTERS, type MonsterKind, type MonsterState } from '../../core/monsters.js';
import { ARTIFACTS, artifactReady } from '../../core/artifacts.js';
import { PASSIVES } from '../../core/passives.js';
import { SKILLS } from '../../core/skills.js';
import { SUMMONS } from '../../core/summons.js';
import { PredictedGame } from './predictedGame.js';
import { createEntityInterpolator, type EntityInterpolator } from './entityInterp.js';
import { saneExtent, saneLifetime, sanedCount, wireRelics, wireSeconds } from './wireSanity.js';
import type { ArtifactSlotView, BarrierSnapshot, TotemSnapshot, HeroSnapshot, LobSnapshot, MatchRosterEntry, PoolSnapshot, ProjectileSnapshot, RevealSnapshot, SnapshotMessage, SummonSnapshot, TimedCommand, TrapSnapshot } from '../../shared/protocol.js';

/* A separately-declared (not `ViewHero & {...}`) mutable shape: intersecting with ViewHero would inherit
   its `readonly` field modifiers, and applySnapshot() below needs to write these every tick. A mutable
   object is freely assignable wherever the readonly ViewHero is expected, just never the reverse. */
export type RemoteHero = {
  id: HeroId; kind: HeroKind; def: HeroDef; team: Team; player: boolean;
  pos: THREE.Vector3; yaw: number; lift: number; hp: number; maxHp: number; alive: boolean; respawnAt: number;
  state: HeroState; stunUntil: number; cd: { attack: number; Q: number; W: number }; swingAt: number;
  visible: boolean; moving: boolean;
  // Status/telegraph flags for net/remoteFx.ts — see protocol.ts's HeroSnapshot doc comment for why each
  // is here and what was deliberately left out.
  slowed: boolean; shielded: boolean; hasted: boolean; enraged: boolean; hasHot: boolean;
  empowered: boolean; feintArmed: boolean; invulnerable: boolean; taunted: boolean; pulled: boolean;
  burning: boolean;
  dash: { dir: { x: number; z: number }; left: number } | null;
  charge: { dir: { x: number; z: number }; k: number } | null;
  hop: { from: { x: number; z: number }; to: { x: number; z: number }; dur: number; arc?: number } | null;
  deadFor: number | null;
  // Artefacts (protocol.ts's HeroSnapshot doc comment on why each of these is here).
  artifacts: { E: ArtifactSlotView | null; R: ArtifactSlotView | null };
  brandFor: number; cloaked: boolean;
  // Астрал (protocol.ts's HeroSnapshot): `forced.until` on this view's own clock, like `stunUntil`.
  invisible: boolean; forced: { kind: 'charm' | 'fear'; until: number } | null; confused: boolean; sleepless: boolean;
  silhouette: boolean;
  // Заклинатели: wolf form's end on this view's clock; Связка, and the far end's spot when the wire gives it.
  wolfUntil: number; tethered: boolean; tetherTo: { x: number; z: number } | null;
};

/** Covers the whole map from its centre, through every wall: an open Око Бестиария (and a replay's "all"). */
export const FULL_SIGHT = (): VisionSource => ({ x: 0, z: 0, r: Math.hypot(LAYOUT.bounds.x, LAYOUT.bounds.z) * 1.05, through: true });

/** A fresh RemoteHero's status fields, all off — shared by RemoteGameView and the replay view. */
export const NO_REMOTE_STATUS = {
  slowed: false, shielded: false, hasted: false, enraged: false, hasHot: false,
  empowered: false, feintArmed: false, invulnerable: false, taunted: false, pulled: false, burning: false,
  dash: null, charge: null, hop: null, deadFor: null,
  artifacts: { E: null, R: null }, brandFor: 0, cloaked: false,
  invisible: false, forced: null, confused: false, sleepless: false, silhouette: false,
  wolfUntil: 0, tethered: false, tetherTo: null,
} as const;

/** GameView.statusOf for a snapshot-fed hero; `t` is the view's own clock. */
export function remoteStatus(r: RemoteHero, t: number): HeroStatus {
  const forced = r.forced && r.forced.until > t ? r.forced : null;
  return {
    slowed: r.slowed, shielded: r.shielded, hasted: r.hasted, enraged: r.enraged, hasHot: r.hasHot,
    empowered: r.empowered, feintArmed: r.feintArmed, invulnerable: r.invulnerable,
    taunted: r.taunted, pulled: r.pulled, cloaked: r.cloaked,
    brandFor: r.brandFor, deadFor: r.deadFor, burning: r.burning,
    invisible: r.invisible, forced: forced?.kind ?? null, forcedFor: forced ? forced.until - t : 0,
    confused: r.confused, sleepless: r.sleepless, silhouette: r.silhouette,
    wolf: r.alive && r.wolfUntil > t, tethered: r.tethered, tetherTo: r.tetherTo,
  };
}

/** A summon's view object for `s`, reused per id (its `pos` a Vector3 mutated in place) — shared by the live
    and replay views. Never trusted blindly: an unknown kind or a non-finite position is dropped. */
export function summonView(cache: Map<number, ViewSummon>, s: SummonSnapshot): ViewSummon | null {
  if (!Object.hasOwn(SUMMONS, s.kind) || !Number.isFinite(s.pos?.x) || !Number.isFinite(s.pos?.z)) return null;
  let v = cache.get(s.id);
  if (!v) { v = { ...s, pos: new THREE.Vector3(s.pos.x, 0, s.pos.z), alive: true }; cache.set(s.id, v); }
  const { pos, ...rest } = s;
  Object.assign(v, rest);
  v.pos.set(pos.x, 0, pos.z);
  return v;
}

/* Created once per den and mutated in place, like RemoteHero. hp/maxHp come straight from each snapshot;
   alive/pos/yaw/state/stateT/moving/visible from interpolateOthers()'s delayed timeline — alive included, so
   a death or respawn never shows ahead of (or at a different spot than) the position it belongs to. */
export type RemoteMonster = {
  id: number; kind: MonsterKind; pos: THREE.Vector3; yaw: number; hp: number; maxHp: number;
  alive: boolean; state: MonsterState; stateT: number; moving: boolean; visible: boolean; brandFor: number;
};

const AIM_INTERVAL = 0.1;    // ~10/s — see apply()'s 'aim' branch
const AIM_DEADZONE = 0.15;   // world units; holding the mouse still costs nothing

// Reconciliation policy (reconcile() below): a correct prediction must be visually indistinguishable from
// no correction at all, so a small disagreement between the replayed prediction and whatever is currently
// on screen eases out over a few frames instead of ever being visible as a snap. SNAP_DIST is set well
// above what an accurate prediction ever disagrees by in normal movement (a hero's fastest base speed is
// ~5.6 units/s, so even a full RTT+jitter's worth of extra lead at 150ms is under a unit of legitimate
// travel) and well below what a real correction — a dash, a leap, a knockback, a respawn — actually moves
// a hero by, so crossing it is a reliable signal that this is one of those, not prediction noise. EASE_RATE
// is chosen so a typical small error (a few centimetres) converges within 2-3 frames (~35-50 ms) — fast
// enough that even an incorrect-but-small prediction is never seen "sliding" for a perceptible fraction of
// a second, slow enough not to look like a snap itself.
const RECONCILE_SNAP_DIST = 1.2;   // world units
const RECONCILE_SNAP_ANGLE = Math.PI / 2;  // a hero doesn't legitimately spin >90° between two frames
const RECONCILE_EASE_RATE = 15;    // 1/s, exponential smoothing
// steerClock(): the client tick is pulled toward its target by gently speeding up or slowing down its own
// clock (by at most CLOCK_SLEW_MAX of real time, 10%), never by stepping it a whole tick: a whole-tick
// nudge renders as a frame that moves the hero two sim steps or none. Rates are per second, so the steering
// is the same at 60 Hz and 144 Hz. Only a drift too big to slew away in about a second (a tab stall, the
// first pong) jumps.
const CLOCK_SNAP_TICKS = 6;
const CLOCK_SLEW_GAIN = 1.8;   // 1/s: fraction of the remaining drift closed per second
const CLOCK_SLEW_MAX = 0.1;
const TICK_MS = STEP_DT * 1000;
/** A snapshot's `countdown` replaces the locally-run one only past this gap (seconds): each snapshot is
    stale by its own one-way trip, so adopting every one would make the digits jitter. */
const COUNTDOWN_RESYNC_S = 0.25;

/** core's own reaction clips (game.ts: damage's 'Hit', kill's 'Death', respawn's 'Idle') — see ownActionClip(). */
const REACTION_CLIPS = new Set(['Hit', 'Death', 'Idle']);
/** How long a clip the prediction played waits for the server's echo of it — comfortably over any RTT
    prediction still runs at (predictedGame.ts's MAX_REPLAY_TICKS is 3 s; real play is well under 1). */
const ECHO_WINDOW_S = 1;
/** A background tab stops the frame loop, and so clearEvents(), while snapshots keep arriving — left alone
    the buffer grows for as long as the tab is hidden, then replays minutes-old hits and sounds at once.
    So once clearEvents() has not run for FRAME_LOOP_IDLE_MS, events more than EVENT_MAX_AGE_TICKS behind
    the newest snapshot are dropped undrawn — except each hero's latest Death/Idle clip, which sets the
    pose its model holds (a corpse, a respawned hero) rather than playing a moment. Gated on the frame loop
    being idle, not on age alone: a delivery stall followed by a burst of old snapshots, with the frame
    loop running, is still every event nobody has seen yet. */
const FRAME_LOOP_IDLE_MS = 500;
const EVENT_MAX_AGE_TICKS = 30;
const STATE_CLIPS = new Set(['Death', 'Idle']);
/** How close a position-only event must be to a unit to count as that unit's — see holdsEvent(). */
const UNIT_EVENT_RADIUS = 1.5;

const angDiff = (a: number, b: number) => Math.atan2(Math.sin(b - a), Math.cos(b - a));

/** See RemoteGameView's own entity-getter comment: returns the same object for the same `id` across
    calls, its fields reassigned to `fresh` in place, so a persistent entity's mesh stays the one mesh
    fx.ts already built for it instead of a new one every snapshot. */
export function memoById<T extends { id: number }>(cache: Map<number, T>, fresh: T): T {
  const v = cache.get(fresh.id);
  if (!v) { cache.set(fresh.id, fresh); return fresh; }
  return Object.assign(v, fresh);
}
export function pruneById<T extends { id: number }>(cache: Map<number, T>, present: readonly T[]) {
  if (cache.size === 0) return;
  const ids = new Set(present.map(x => x.id));
  for (const id of cache.keys()) if (!ids.has(id)) cache.delete(id);
}

export class RemoteGameView implements GameView {
  readonly heroes: RemoteHero[];
  readonly points: ViewPoint[];
  events: GameEvent[] = [];
  /** Wall time of the last clearEvents(), or of the first snapshot before any — see FRAME_LOOP_IDLE_MS. */
  private lastClearAtMs: number | null = null;
  /** The server tick each entry of `events` arrived with — see EVENT_MAX_AGE_TICKS. */
  private eventTicks: number[] = [];
  /** Events about another hero or a monster, held until the interpolation clock reaches the tick they
      arrived with — see holdsEvent(). */
  private heldEvents: { e: GameEvent; tick: number }[] = [];
  /** Own action clips the prediction has played, awaiting the server's echo — see ownActionClip(). */
  private playedClips: { key: string; at: number }[] = [];
  t = 0;
  clock = 0;
  /** Seconds of pre-match countdown left (GameView.countdown), run locally by tick() from matchStart's value. */
  countdown = 0;
  score = { a: 0, b: 0 };
  overtime = false;
  winner: Team | null = null;
  winnerT = 0;
  /** Set by net/onlineMatch.ts from `matchEnd`, never from a snapshot. */
  matchStats: MatchStats | null = null;
  readonly player: RemoteHero;
  private readonly allMonsters: RemoteMonster[];
  /** Pre-filtered like every other GameView entity list — see ViewMonster's own header. */
  get monsters(): readonly ViewMonster[] { return this.allMonsters.filter(m => m.visible && m.alive); }
  teamBuffs: ViewTeamBuffs = { a: { behemoth: null, griffin: null }, b: { behemoth: null, griffin: null } };
  relics: Record<Team, RelicLine | null> = { a: null, b: null };
  /** The own team's Око Бестиария closes then, on this view's clock. */
  private eyeUntil = 0;
  /** The last snapshot's own ackSeq — net/onlineMatch.ts feeds this to the command channel's ack(). */
  ackSeq = 0;
  private wireYou: SnapshotMessage['you'] = {
    cdQ: 0, cdW: 0, charge: null, ink: 0, inBase: false,
    artifactCdFor: { E: 0, R: 0 }, artifactLocks: [],
    effects: { slows: [], haste: [], taunt: null, pull: null, cloakFor: 0 },
  };
  /** `reveals` are also read by visionSources() below, the same reveals core's own formula folds in —
      own-team-only on the wire already. Every one of these
      seven is already exactly its matching GameView `View*` shape (protocol.ts's own `*Snapshot` types were
      designed to be — see ViewPool's own header) — stored and handed back verbatim, no conversion needed. */
  private rawPools: PoolSnapshot[] = [];
  private rawTraps: TrapSnapshot[] = [];
  private rawBarriers: BarrierSnapshot[] = [];
  private rawReveals: RevealSnapshot[] = [];
  private rawTotems: TotemSnapshot[] = [];
  private rawProjectiles: ProjectileSnapshot[] = [];
  private rawLobs: LobSnapshot[] = [];
  private rawSummons: SummonSnapshot[] = [];
  private readonly summonViews = new Map<number, ViewSummon>();
  get summons(): readonly ViewSummon[] {
    return sanedCount(this.rawSummons, 'summons').map(s => summonView(this.summonViews, s)).filter((v): v is ViewSummon => v !== null);
  }
  /** Every getter below refuses a wire value core itself could never produce (wireSanity.ts's own header
      says why this lives here and nowhere else) AND returns the SAME object per wire `id` across snapshots
      — each snapshot's own pools/totems/etc. are fresh plain objects (unlike heroes, never mutated in
      place), so fx.ts's `visualFor(owner, build)` cache, keyed by object identity, would otherwise treat
      every snapshot's entry as a brand-new entity and rebuild its mesh from scratch each time — exactly the
      "redrawn from scratch each snapshot" this project's own artefact task was written to avoid, and the
      reason a struck placed object's hit marker used to never actually go dark online (the update landed on
      an abandoned Group, not the one still on screen). Mirrors gameView.ts's own `memoView`, keyed by the
      wire's numeric `id` instead of object identity since these objects are never the same reference twice. */
  private readonly poolViews = new Map<number, ViewPool>();
  private readonly trapViews = new Map<number, ViewTrap>();
  private readonly barrierViews = new Map<number, ViewBarrier>();
  private readonly revealViews = new Map<number, ViewReveal>();
  private readonly totemViews = new Map<number, ViewTotem>();
  private readonly projectileViews = new Map<number, ViewProjectile>();
  private readonly lobViews = new Map<number, ViewLob>();
  get pools(): readonly ViewPool[] { return sanedCount(this.rawPools, 'pools').filter(z => saneExtent(z.r, 'pool.r') && saneLifetime(z.left, 'pool.left') && saneLifetime(z.life, 'pool.life')).map(z => memoById(this.poolViews, z)); }
  get traps(): readonly ViewTrap[] { return sanedCount(this.rawTraps, 'traps').filter(t => saneExtent(t.r, 'trap.r') && saneLifetime(t.armIn, 'trap.armIn') && saneLifetime(t.left, 'trap.left')).map(t => memoById(this.trapViews, t)); }
  get barriers(): readonly ViewBarrier[] { return sanedCount(this.rawBarriers, 'barriers').filter(b => saneExtent(b.half, 'barrier.half') && saneLifetime(b.left, 'barrier.left')).map(b => memoById(this.barrierViews, b)); }
  get reveals(): readonly ViewReveal[] { return sanedCount(this.rawReveals, 'reveals').filter(r => saneExtent(r.r, 'reveal.r') && saneLifetime(r.left, 'reveal.left')).map(r => memoById(this.revealViews, r)); }
  get totems(): readonly ViewTotem[] { return sanedCount(this.rawTotems, 'totems').filter(t => saneExtent(t.r, 'totem.r') && saneLifetime(t.left, 'totem.left')).map(b => memoById(this.totemViews, b)); }
  get projectiles(): readonly ViewProjectile[] { return sanedCount(this.rawProjectiles, 'projectiles').map(p => memoById(this.projectileViews, p)); }
  get lobs(): readonly ViewLob[] { return sanedCount(this.rawLobs, 'lobs').filter(l => saneExtent(l.r, 'lob.r')).map(l => memoById(this.lobViews, l)); }
  get you(): YouStats {
    const w = this.wireYou;
    // `effects` is absent from a pre-protocol-4 `you`; the HUD then simply shows no timed effects.
    const effects = w.effects && {
      slows: w.effects.slows, haste: w.effects.haste, cloakFor: w.effects.cloakFor,
      ...(w.effects.stillFor !== undefined ? { stillFor: w.effects.stillFor } : {}),
      ...(w.effects.quietFor !== undefined ? { quietFor: w.effects.quietFor } : {}),
    };
    return { ink: w.ink, inBase: w.inBase, artifactLocks: w.artifactLocks, effects, relicCd: wireSeconds(w.relicCd), eyeFor: Math.max(0, this.eyeUntil - this.t) };
  }
  private lastAimAt = -9;
  private lastAim: { x: number; z: number } | null = null;
  /** The newest aim the throttle held back, if any — flushed ahead of a `release` (see apply()). */
  private heldAim: Command | null = null;
  /** DebugTransport (and, in principle, any unordered transport) delays each message independently, so
      two snapshots can arrive in the opposite order they were sent. Applying an older one after a newer
      one already landed would visibly roll back score/winner/positions — a stale-tick guard, not a
      throttle: every in-order snapshot is still applied exactly as before. */
  private lastAppliedTick = -1;

  /** The client's own fixed-1/60s tick counter — advanced by tick() below, occasionally nudged by
      net/onlineMatch.ts toward net/clockSync.ts's estimate of "the server's current tick + lead". This is
      the timeline predictedGame.ts's replay runs on, and what commandChannel.ts tags outgoing commands
      with. Public and plain, not a getter: onlineMatch.ts needs to both read it (for the command channel's
      clock) and occasionally snap it (a clock correction), and there is no invariant a setter would need
      to protect here. */
  clientTick = 0;
  /** The tick a command issued right now takes effect on: the next one, not clientTick itself — the
      render already shows part of the way from clientTick to the next tick (see reconcile()), so an order
      applied at clientTick would rewrite motion that is already on screen. */
  get inputTick() { return this.clientTick + 1; }
  private tickAcc = 0;
  private readonly predicted: PredictedGame;
  private readonly interp: EntityInterpolator = createEntityInterpolator();
  /** The RAW predicted pose from the previous reconcile() call (before any easing) — see reconcile()'s own
      comment on why the offset to decay must be measured against this, not against last frame's RENDERED
      pose, or a steadily-moving prediction develops a permanent lag behind itself. */
  private lastRawPos: { x: number; z: number } | null = null;
  private lastRawYaw: number | null = null;
  /** How far behind the server's live tick advanceOthers() last drew everyone else, ms — see interpDelayMs. */
  private lastTotalDelayMs: number | null = null;

  constructor(roster: MatchRosterEntry[], yourHero: HeroId, seed: number, private readonly sendCommand: (heroId: HeroId, cmd: Command) => void) {
    this.predicted = new PredictedGame(roster, yourHero, seed);
    this.heroes = roster.map((r): RemoteHero => {
      const def = HEROES[r.hero];
      const [bx, bz] = LAYOUT.bases[r.team];
      return {
        id: r.seat, kind: r.hero, def, team: r.team, player: r.seat === yourHero,
        pos: new THREE.Vector3(bx, 0, bz), yaw: 0, lift: 0, hp: def.hp, maxHp: def.hp, alive: true, respawnAt: 0,
        state: 'normal', stunUntil: 0, cd: { attack: 0, Q: 0, W: 0 }, swingAt: -9,
        visible: r.seat === yourHero || r.team === roster[yourHero]!.team, moving: false,
        ...NO_REMOTE_STATUS, artifacts: { E: null, R: null },
      };
    });
    this.player = this.heroes[yourHero]!;
    this.points = LAYOUT.points.map(p => ({ id: p.id, pos: { x: p.pos[0], z: p.pos[1] }, owner: null, progress: 0, contested: false }));
    this.allMonsters = LAYOUT.bosses.map((b, id): RemoteMonster => ({
      id, kind: b.kind, pos: new THREE.Vector3(b.pos[0], 0, b.pos[1]), yaw: 0,
      hp: MONSTERS[b.kind].hp, maxHp: MONSTERS[b.kind].hp, alive: true, state: 'idle', stateT: 0, moving: false, visible: false, brandFor: 0,
    }));
  }

  /** matchStart's (or a mid-countdown reconnect's) `countdown`. The client clock starts that many ticks
      below zero, like the server's pong.tick, so it reaches tick 0 together with the server's first real
      tick even before any pong has landed — no clock snap at the start of play. */
  startCountdown(seconds: number) {
    this.countdown = Math.max(0, seconds);
    this.clientTick = -Math.round(this.countdown / STEP_DT);
    this.tickAcc = 0;
  }

  applySnapshot(msg: SnapshotMessage, nowMs = Date.now()) {
    // Before the stale-tick guard, which would skip every countdown snapshot after the first (all tick 0).
    const cd = msg.countdown ?? 0;
    if (msg.tick >= this.lastAppliedTick && (cd === 0 || Math.abs(cd - this.countdown) > COUNTDOWN_RESYNC_S)) this.countdown = cd;
    // Buffered regardless of the stale-tick guard just below: entityInterp.ts wants every tick it can get
    // to fill gaps in its own history, and de-dupes by tick itself — it isn't a sequential-apply consumer
    // the way the rest of this method is.
    this.interp.ingest(msg, nowMs);
    // Before the stale-tick guard, and appended rather than assigned: every snapshot carries only the
    // events since the previous one, so one arriving late, or two landing in the same frame, still has
    // events nobody has shown yet. Kept until the frame loop's clearEvents() — see FRAME_LOOP_IDLE_MS.
    for (const e of msg.events) {
      if (this.holdsEvent(e, msg)) this.heldEvents.push({ e, tick: msg.tick });
      else if (!this.isEchoOfPlayedClip(e)) this.pushEvent(e, msg.tick);
    }
    this.lastClearAtMs ??= nowMs;
    if (nowMs - this.lastClearAtMs > FRAME_LOOP_IDLE_MS) this.trimUndrawnEvents(Math.max(msg.tick, this.lastAppliedTick));
    if (msg.tick <= this.lastAppliedTick) return;  // stale/reordered — see lastAppliedTick's comment
    this.lastAppliedTick = msg.tick;
    this.clock = msg.clock;
    this.score = msg.score;
    this.overtime = msg.overtime;
    if (this.winner === null && msg.winner !== null) this.winnerT = 0;  // just landed — start the countdown
    this.winner = msg.winner;
    this.ackSeq = msg.ackSeq;
    this.wireYou = msg.you;
    this.rawPools = msg.pools;
    this.rawTraps = msg.traps;
    this.rawBarriers = msg.barriers;
    this.rawReveals = msg.reveals;
    this.rawTotems = msg.totems;
    this.rawProjectiles = msg.projectiles;
    this.rawSummons = msg.summons ?? [];
    pruneById(this.summonViews, this.rawSummons);
    // A lob's `t0` is on the server's match clock; fx.ts measures its flight against this view's `t` (as
    // replayView.ts does). Converted once per lob, when first seen: re-deriving it from every snapshot's
    // arrival time would make the flight jitter with the network.
    const matchNow = msg.tick * STEP_DT;
    this.rawLobs = msg.lobs.map(l => ({ ...l, t0: this.lobViews.get(l.id)?.t0 ?? this.t - (matchNow - l.t0) }));
    // Drops a cached View* the moment its id stops appearing at all (gone from the wire, or fog-hidden
    // this tick) — bounded growth over a long match, and correct either way: fx.ts's own "untouched this
    // frame" cleanup would hide/remove the mesh regardless, this just stops memoById() handing back a
    // reference to an entity that may already be gone for good.
    pruneById(this.poolViews, msg.pools);
    pruneById(this.trapViews, msg.traps);
    pruneById(this.barrierViews, msg.barriers);
    pruneById(this.revealViews, msg.reveals);
    pruneById(this.totemViews, msg.totems);
    pruneById(this.projectileViews, msg.projectiles);
    pruneById(this.lobViews, msg.lobs);
    this.predicted.resync(msg);
    // The only place a REAL accuracy figure exists: predictedGame.ts just compared what it predicted for
    // this exact tick against what the server actually reported for it. Left at its previous value on a
    // `null` (no buffered prediction to compare against yet) rather than reset to 0 — a gap in coverage is
    // not the same claim as a measured perfect prediction.
    if (this.predicted.lastResyncErrorM !== null) this.stats.predictionErrorM = this.predicted.lastResyncErrorM;
    // Every other hero's hp/alive/statuses come from the interpolated frame instead (interpolateOthers), so
    // a death or respawn lands where and when its position does.
    const own = msg.heroes[this.player.id];
    const wasAlive = this.player.alive;
    if (own) applyHeroSnapshot(this.player, own, this.t);
    // The prediction only ever ran ahead of the server, so it never saw this death: the body falls where the
    // server says it did, not where the prediction had it walking on to.
    if (own?.visible && wasAlive && !own.alive) this.player.pos.set(own.pos.x, 0, own.pos.z);
    // Own hero's Q/W cooldown lives on `you` (leaking a rival's would be the same information asymmetry
    // fog-of-war already avoids) — copy it onto the ViewHero field the shared hud.ts/input.ts already
    // read generically as `p.cd[k]`, instead of widening HeroSnapshot for a value already on the wire.
    this.player.cd.Q = msg.you.cdQ;
    this.player.cd.W = msg.you.cdW;
    // `?? []`/`??` fallbacks: a pre-monster server's snapshot must degrade to "no monsters", not crash.
    for (const ms of msg.monsters ?? []) {
      const m = this.allMonsters[ms.id];
      if (!m || !ms.visible) continue;  // fog: last-seen values freeze, same as a hero
      m.hp = ms.hp; m.maxHp = ms.maxHp; m.brandFor = ms.brandFor;
    }
    if (msg.teamBuffs) this.teamBuffs = msg.teamBuffs;
    this.relics = wireRelics(msg.relics);
    this.eyeUntil = this.t + wireSeconds(msg.you.eyeFor);
    for (const ps of msg.points) {
      const p = this.points.find(x => x.id === ps.id);
      if (!p) continue;
      p.owner = ps.owner; p.progress = ps.progress;
      // Derived, not sent: an enemy contesting a point you can't otherwise see would leak exactly what
      // fog-of-war hides (see protocol.ts's PointSnapshot comment) — so this only ever counts heroes this
      // view can actually see, same rule core's own updatePoints() applies with the real (unfiltered) list.
      const R = LAYOUT.pointRadius;
      const near: { team: Team }[] = this.heroes.filter(h => h.alive && h.visible && Math.hypot(h.pos.x - p.pos.x, h.pos.z - p.pos.z) < R);
      for (const s of this.rawSummons) if (s.kind === 'golem' && Math.hypot(s.pos.x - p.pos.x, s.pos.z - p.pos.z) < R) near.push(s);  // a golem counts as a player
      p.contested = near.some(h => h.team === 'a') && near.some(h => h.team === 'b');
    }
  }

  /** The player's own action clips (a swing, a cast's windup) are played from the prediction the moment
      they happen locally; the server's copy of the same event lands about an RTT later and would restart
      the clip mid-way, so an echo of a clip the prediction really played is dropped — and only such an
      echo: one the prediction never played (it refused a cast the server accepted, or was not running)
      still plays. Reactions to what someone ELSE did (a hit, a death, a respawn) are the server's alone —
      the shadow Game's own versions of those are its bots' invention (see reconcile()). Returns the
      clip's matching key, or null for any other event. */
  private ownActionClip(e: GameEvent): string | null {
    if (e.type !== 'anim' || e.hero !== this.player.id || e.clips.some(c => REACTION_CLIPS.has(c))) return null;
    return e.clips.join('|');
  }

  private isEchoOfPlayedClip(e: GameEvent) {
    const key = this.ownActionClip(e);
    if (key === null) return false;
    this.playedClips = this.playedClips.filter(c => this.t - c.at <= ECHO_WINDOW_S);
    const i = this.playedClips.findIndex(c => c.key === key);
    if (i < 0) return false;
    this.playedClips.splice(i, 1);
    return true;
  }

  /** Another hero's or a monster's event is drawn when that unit's interpolated pose reaches the tick it
      arrived with, not up to an interpolation delay ahead of it (a death clip on a still-walking body, a
      damage number before the swing lands). An event that only names a position (a hit, a splat, a death
      burst) belongs to the unit standing on it in the same snapshot, if any, and waits with that unit's
      own; one on the own hero or on open ground goes out at once, like the projectiles and pools drawn
      from the newest snapshot. Relic events name neither and are never held. */
  private holdsEvent(e: GameEvent, msg: SnapshotMessage): boolean {
    if ('monster' in e) return true;
    if ('hero' in e) return e.hero !== this.player.id;
    if (!('pos' in e)) return false;
    let nearest = UNIT_EVENT_RADIUS, own = false, found = false;
    const consider = (pos: { x: number; z: number }, isOwn: boolean) => {
      const d = Math.hypot(pos.x - e.pos.x, pos.z - e.pos.z);
      if (d < nearest) { nearest = d; own = isOwn; found = true; }
    };
    for (const hs of msg.heroes) if (hs.visible) consider(hs.pos, hs.id === this.player.id);
    for (const ms of msg.monsters ?? []) if (ms.visible) consider(ms.pos, false);
    return found && !own;
  }

  private releaseHeldEvents(upToTick: number) {
    if (this.heldEvents.length === 0) return;
    const due = this.heldEvents.filter(h => h.tick <= upToTick);
    if (due.length === 0) return;
    this.heldEvents = this.heldEvents.filter(h => h.tick > upToTick);
    for (const h of due) this.pushEvent(h.e, h.tick);
  }

  private trimUndrawnEvents(newestTick: number) {
    // The frame loop is not running, so nothing will release these: fold them in to be trimmed like the rest.
    this.releaseHeldEvents(Infinity);
    const cutoff = newestTick - EVENT_MAX_AGE_TICKS;
    const latestState = new Map<HeroId, number>();
    this.events.forEach((e, i) => {
      if (this.eventTicks[i]! < cutoff && e.type === 'anim' && e.clips.some(c => STATE_CLIPS.has(c))) latestState.set(e.hero, i);
    });
    const keep = this.events.map((e, i) => this.eventTicks[i]! >= cutoff || (e.type === 'anim' && latestState.get(e.hero) === i));
    this.events = this.events.filter((_, i) => keep[i]);
    this.eventTicks = this.eventTicks.filter((_, i) => keep[i]);
  }

  private pushEvent(e: GameEvent, tick: number) {
    this.events.push(e);
    this.eventTicks.push(tick);
  }

  /** Advances the presentational clock AND the fixed-1/60s tick counter prediction and the outgoing
      command channel both run on. Real wall-clock dt, clamped the same way the offline frame loop clamps
      its own (a stalled tab shouldn't jump the aim-mark timers, or fast-forward the tick counter by
      hundreds of ticks). `winnerT` counts up the same way once a winner lands, so the victory banner's
      countdown actually moves instead of sitting frozen at its starting value. */
  tick(dt: number) {
    const clamped = Math.min(dt, 0.25);
    this.t += clamped;
    this.countdown = Math.max(0, this.countdown - clamped);
    if (this.winner !== null) this.winnerT += clamped;
    this.tickAcc += clamped;
    this.normalizeTickAcc();
  }

  /** Steers clientTick toward `targetTick` (net/clockSync.ts's "server tick now + lead") — see
      CLOCK_SLEW_GAIN. Returns how many whole ticks clientTick jumped (0 when it only slewed), which the
      caller passes to commandChannel.rebaseTicks() so already-tagged commands keep their place. */
  steerClock(targetTick: number, dt: number): number {
    const drift = targetTick - (this.clientTick + this.tickAcc / STEP_DT);
    if (Math.abs(drift) > CLOCK_SNAP_TICKS) {
      const jump = Math.floor(targetTick) - this.clientTick;
      this.clientTick = Math.floor(targetTick);
      this.tickAcc = (targetTick - this.clientTick) * STEP_DT;
      return jump;
    }
    const step = Math.min(dt, 0.25);
    this.tickAcc += Math.max(-CLOCK_SLEW_MAX * step, Math.min(CLOCK_SLEW_MAX * step, drift * STEP_DT * CLOCK_SLEW_GAIN * step));
    this.normalizeTickAcc();
    return 0;
  }

  private normalizeTickAcc() {
    while (this.tickAcc >= STEP_DT) { this.tickAcc -= STEP_DT; this.clientTick++; }
    while (this.tickAcc < 0) { this.tickAcc += STEP_DT; this.clientTick--; }
  }

  /** Called once a frame by net/onlineMatch.ts with the input buffer it owns: replays the player's own
      unacked commands against the shadow Game (net/predictedGame.ts) up to the tick after clientTick, then
      renders the position interpolated between that tick and the one before by how far into the current
      tick the client clock is (fixed-timestep render interpolation: a 60 Hz sim drawn at a display rate
      that is never exactly 60 Hz otherwise moves the hero two steps on some frames and none on others),
      and blends the result into the rendered pose — smoothly if the disagreement with what is already on
      screen is small (prediction was very nearly right), snapping instantly if it is large (a real
      correction: a dash, a leap, a knockback, a respawn). See RECONCILE_SNAP_DIST's own comment for why
      those thresholds are where they are. A frame before the first snapshot ever lands, there is nothing
      to reconcile against yet, so this is a no-op — the player's hero simply sits at its spawn pose. */
  reconcile(dt: number, standingOrder: TimedCommand | null, unacked: readonly TimedCommand[]) {
    const result = this.predicted.predict(standingOrder, unacked, this.clientTick + 1);
    if (!result) return;
    const { pose, prevPos, correction, animEvents } = result;
    const p = this.player;
    // Cooldowns as predicted for the rendered instant: a cast shows its cooldown the moment it goes out and
    // the sweep runs every frame, instead of at 20 Hz an RTT late.
    p.cd.attack = pose.cd.attack; p.cd.Q = pose.cd.Q; p.cd.W = pose.cd.W;
    // Dead on the server: the body stays where it fell until the server's respawn lands. The shadow's own
    // respawn at the fountain runs a lead ahead of it and would draw the corpse there for about an RTT.
    if (!p.alive) { this.lastRawPos = null; this.lastRawYaw = null; return; }
    // No interpolation across a teleport (a respawn, a blink): it would draw the hero mid-way for a frame.
    const alpha = Math.hypot(pose.pos.x - prevPos.x, pose.pos.z - prevPos.z) > RECONCILE_SNAP_DIST ? 1 : this.tickAcc / STEP_DT;
    const rawX = prevPos.x + (pose.pos.x - prevPos.x) * alpha, rawZ = prevPos.z + (pose.pos.z - prevPos.z) * alpha;
    const k = 1 - Math.exp(-RECONCILE_EASE_RATE * Math.min(dt, 0.25));

    // How far the RAW prediction currently sits from what's on screen — the "is this a real correction"
    // signal (unchanged meaning from before). What changed is what happens in the ease branch: the offset
    // being decayed is measured against last frame's RAW prediction (this.lastRawPos), never against last
    // frame's RENDERED position — easing the rendered position directly toward a target that is itself
    // moving every frame has a steady-state lag proportional to speed/EASE_RATE (a hero walking at 5.6
    // units/s left a constant, latency-independent 0.33m gap behind its own prediction under the older
    // version of this code — see remoteGame.test.ts's regression test). Decaying the offset instead
    // converges exactly onto a moving-but-correct prediction: once there is no real disagreement left, the
    // offset shrinks toward zero every frame regardless of how fast the target itself is moving, so a
    // correct prediction is genuinely indistinguishable from no correction at all, not merely close to it.
    // A correction the replay itself reports (a snapshot or an ack moved the prediction for a tick it had
    // already predicted) is folded into that offset first: the raw prediction jumps by it, the render
    // must not — it eases out over the next frames like any other disagreement.
    const dist = Math.hypot(rawX - p.pos.x, rawZ - p.pos.z);
    if (dist > RECONCILE_SNAP_DIST || !this.lastRawPos) {
      p.pos.set(rawX, 0, rawZ);
    } else {
      const absorb = correction && Math.hypot(correction.x, correction.z) <= RECONCILE_SNAP_DIST ? correction : null;
      const offsetX = p.pos.x - this.lastRawPos.x - (absorb?.x ?? 0), offsetZ = p.pos.z - this.lastRawPos.z - (absorb?.z ?? 0);
      p.pos.set(rawX + offsetX * (1 - k), 0, rawZ + offsetZ * (1 - k));
    }

    const dYaw = angDiff(p.yaw, pose.yaw);
    if (Math.abs(dYaw) > RECONCILE_SNAP_ANGLE || this.lastRawYaw === null) {
      p.yaw = pose.yaw;
    } else {
      const yawCorrection = correction ? angDiff(0, correction.yaw) : 0;
      const yawOffset = angDiff(this.lastRawYaw, p.yaw) - (Math.abs(yawCorrection) <= RECONCILE_SNAP_ANGLE ? yawCorrection : 0);
      p.yaw = pose.yaw + yawOffset * (1 - k);
    }
    this.lastRawPos = { x: rawX, z: rawZ };
    this.lastRawYaw = pose.yaw;

    p.lift = pose.lift;
    p.state = pose.state;
    // The shadow's clock is its tick count; heroView.ts reads `swingAt` against this view's own `t`.
    const renderSimT = (this.clientTick + this.tickAcc / STEP_DT) * STEP_DT;
    p.swingAt = Math.min(this.t, this.t - (renderSimT - pose.swingAt));
    p.moving = pose.moving;
    for (const e of animEvents) {
      const key = this.ownActionClip(e);
      if (key === null) continue;
      this.playedClips.push({ key, at: this.t });
      this.pushEvent(e, this.lastAppliedTick);
    }

    this.stats.lastCorrectionWasSnap = dist > RECONCILE_SNAP_DIST;
    if (this.stats.lastCorrectionWasSnap) this.stats.snapCount++;
  }

  /** Read by the `?net` overlay (net/onlineMatch.ts) and by tests — not part of the GameView contract.
      `predictionErrorM` is set from applySnapshot()'s comparison of a past prediction against what the
      server actually reported for that same tick (predictedGame.ts's lastResyncErrorM) — real server
      information, not a measurement of the render smoother's own lag behind itself. `null`, not 0, until
      that comparison has actually happened once: a stage-3 review caught that an initial value of 0 reads
      as a CONFIDENT claim of perfect prediction to anything downstream (the `?net` overlay, a test) that
      cannot otherwise tell "measured zero error" apart from "never measured anything at all" — exactly the
      false reassurance the fixed prediction-error metric was supposed to have ended. */
  readonly stats: { predictionErrorM: number | null; lastCorrectionWasSnap: boolean; snapCount: number }
    = { predictionErrorM: null, lastCorrectionWasSnap: false, snapCount: 0 };

  /** Called once a frame by net/onlineMatch.ts, mirroring reconcile() but for every hero that ISN'T the
      player's own: advances net/entityInterp.ts's own continuous render clock (see that module's header)
      by `dt` and draws everyone else at it — the "render slightly behind" mirror of reconcile()'s "render
      slightly ahead". `estimatedServerTick` (net/clockSync.ts's, null before the first pong) only measures
      how far behind the server's live tick that is, for interpDelayMs. */
  advanceOthers(dt: number, nowMs: number, estimatedServerTick: number | null) {
    const renderTick = this.interp.advance(dt, nowMs);
    if (renderTick === null) return;
    if (estimatedServerTick !== null) this.lastTotalDelayMs = Math.max(0, estimatedServerTick - renderTick) * TICK_MS;
    this.interpolateOthers(renderTick);
  }

  /** Draws every other hero and monster as of `renderTick` on the snapshot timeline, and releases the events
      held for it. A `null` sample (nothing buffered for this hero yet at all — before the very first
      snapshot) leaves the hero exactly where it started (its spawn placeholder), same as reconcile()'s own
      before-any-data no-op. */
  interpolateOthers(renderTick: number) {
    for (const h of this.heroes) {
      if (h.player) continue;
      const pose = this.interp.sample(h.id, renderTick);
      if (!pose) continue;
      if (!pose.visible) { h.visible = false; continue; }
      h.visible = true;
      h.pos.set(pose.pos.x, 0, pose.pos.z);
      h.yaw = pose.yaw;
      h.lift = pose.lift;
      h.state = pose.state as HeroState;
      h.moving = pose.moving;
      // Its seconds-left fields count from the frame's own tick, which the render instant is past by now.
      applyHeroSnapshot(h, pose.from, this.t - pose.sinceTicks * STEP_DT);
    }
    for (const m of this.allMonsters) {
      const pose = this.interp.sampleMonster(m.id, renderTick);
      if (!pose) continue;
      if (!pose.visible) { m.visible = false; continue; }
      m.visible = true;
      m.alive = pose.alive;
      m.pos.set(pose.pos.x, 0, pose.pos.z);
      m.yaw = pose.yaw;
      m.state = pose.state as MonsterState;
      m.stateT = pose.stateT;
      m.moving = pose.moving;
    }
    this.releaseHeldEvents(renderTick);
  }

  /** The TOTAL delay every other hero is currently rendered behind the server's live tick — one-way latency
      plus entityInterp.ts's own adaptive margin, not the margin alone (an earlier version reported only
      the margin, which understated the real figure above ~60ms of latency). Read by the `?net` overlay and
      reported to the server (protocol.ts's `interpDelay`) so its lag compensation can rewind by what this
      client actually achieves, not config.ts's placeholder. The margin alone until it is first measured. */
  get interpDelayMs() { return this.lastTotalDelayMs ?? this.interp.delayMs; }

  /** Snapshot arrivals discarded as a likely local stall rather than real jitter — see entityInterp.ts's
      own `stallCount` and the `?net` overlay, which surfaces this so a contaminated-looking delay is
      explained rather than hidden. */
  get interpStalls() { return this.interp.stallCount; }

  apply(heroId: HeroId, cmd: Command) {
    if (this.countdown > 0) return;  // the server would drop it anyway (match.ts)
    // The server only ever applies a command to the sender's own seat regardless of what's sent (see
    // match.ts) — heroId is accepted here only to keep GameView's signature identical to LocalGameView's,
    // not because a different value would do anything different server-side.
    void heroId;
    // input.ts calls apply({type:'aim'}) every frame while charging/channeling, unthrottled, on purpose —
    // the local aim-line visual reads groundAt() directly and is instant either way, and offline that
    // per-frame call is free. Online it is not: 60/s blows through the server's own command bucket
    // (config.ts: 20/s refill, costed by batch size) and the moves/casts sharing that bucket pay for it
    // too. Throttle strictly HERE, not in input.ts, so offline stays a genuinely unthrottled direct call
    // and this network concern never leaks into shared render/input code.
    if (cmd.type === 'aim') {
      const moved = !this.lastAim || Math.hypot(cmd.x - this.lastAim.x, cmd.z - this.lastAim.z) > AIM_DEADZONE;
      if (!moved || this.t - this.lastAimAt < AIM_INTERVAL) { this.heldAim = cmd; return; }
      this.lastAimAt = this.t;
      this.lastAim = { x: cmd.x, z: cmd.z };
      this.heldAim = null;
    }
    // A release fires along whatever the server last heard as the aim, so the aim the throttle is still
    // holding goes out first — otherwise the shot leaves up to AIM_INTERVAL stale.
    if (cmd.type === 'release' && this.heldAim) {
      this.sendCommand(this.player.id, this.heldAim);
      this.heldAim = null;
    }
    this.sendCommand(this.player.id, cmd);
  }
  clearEvents() { this.events = []; this.eventTicks = []; this.lastClearAtMs = Date.now(); }
  visibleTo(_team: Team, h: ViewHero) { return (h as RemoteHero).visible; }
  movingOf(h: ViewHero) { return (h as RemoteHero).moving; }
  chargeLevel(h: ViewHero) { return h === this.player ? (this.wireYou.charge ?? 0) : ((h as RemoteHero).charge?.k ?? 0); }
  /** Approximates the server's own gate (core's Game.canCast: alive, off cooldown, in `normal` state, not
      stunned, taunted, hooked or cloaked) for the *cosmetic* purposes input.ts uses it for (an aim-cursor hint, a
      hit-marker prediction) — the server is still the one true gate; a stale guess here at worst shows
      a marker for a cast that then silently fails, never the reverse. */
  canCast(h: ViewHero, key: SkillKey) {
    if (h !== this.player) return false;
    return h.alive && this.player.cd[key] <= 0 && this.freeToAct(h as RemoteHero);
  }

  private freeToAct(h: RemoteHero) {
    return h.state === 'normal' && h.stunUntil <= 0 && !h.taunted && !h.pulled && !h.cloaked && !(h.forced && h.forced.until > this.t);
  }

  /** Same cosmetic-approximation contract as canCast() above, plus core's artefact-only gates
      (artifacts.ts's canCastArtifact): an empty or passive slot. */
  canCastArtifact(h: ViewHero, slot: ArtifactSlotKey) {
    if (h !== this.player) return false;
    const rh = h as unknown as RemoteHero;
    const s = rh.artifacts[slot];
    if (!s || ARTIFACTS[s.line].kind !== 'active') return false;
    return h.alive && artifactReady({ line: s.line, cd: this.wireYou.artifactCdFor[slot] }) && this.freeToAct(rh);
  }
  artifactCdFor(h: ViewHero, slot: ArtifactSlotKey) { return h === this.player ? this.wireYou.artifactCdFor[slot] : 0; }
  /** Same cosmetic contract as canCastArtifact(): the team's relic is an active one and off its shared cooldown. */
  canCastRelic(h: ViewHero) {
    const line = this.relics[this.player.team];
    if (h !== this.player || !line || RELICS[line].kind !== 'active') return false;
    return h.alive && wireSeconds(this.wireYou.relicCd) <= 0 && this.freeToAct(h as RemoteHero);
  }
  telegraphOf(h: ViewHero): HeroTelegraph {
    const r = h as unknown as RemoteHero;
    return { dash: r.dash, hop: r.hop };
  }
  statusOf(h: ViewHero): HeroStatus { return remoteStatus(h as unknown as RemoteHero, this.t); }
  /** As of the last snapshot: `stillFor` is capped at Сошка's threshold, so reaching it means the bonus is on. */
  attackRangeOf(h: ViewHero) {
    if ((h as RemoteHero).alive && (h as RemoteHero).wolfUntil > this.t) return SKILLS.shewolf.W.range;
    const P = PASSIVES.arquebusier, still = this.wireYou.effects?.stillFor ?? 0;
    return h === this.player && h.kind === 'arquebusier' && still >= P.still ? h.def.range + P.bonus : h.def.range;
  }
  maxHpOf(h: ViewHero) { return (h as unknown as RemoteHero).maxHp; }
  /** A pure function of position on the shared map, so the client answers it exactly as the server would. */
  groundSpeedAt(x: number, z: number) { return groundSpeed(x, z); }
  /** Never answerable online — protocol.ts's HeroSnapshot deliberately never sends `taunt.by`/`pull.by` (a
      tether target's position would leak fog if that hero isn't independently visible); fx.ts's own
      tether-line visual is offline-only for exactly this reason, same as remoteFx.ts's existing "a plain
      presence marker, not a tether" simplification. */
  tetherSource(_h: ViewHero): ViewHero | null { return null; }
  inSight(team: Team, pos: { x: number; z: number }) {
    return this.visionSources(team).some(s => Math.hypot(s.x - pos.x, s.z - pos.z) < s.r);
  }

  /** Reconstructs core's own visionSources() formula for the viewer's own team only (own base + own
      visible heroes + own-team reveals) — always correct for that team, since "your own
      team" is never fog-gated in the first place. Only ever called with the viewer's own team
      (matchView.ts's drawVision). */
  visionSources(team: Team): VisionSource[] {
    if (team === this.player.team && this.t < this.eyeUntil) return [FULL_SIGHT()];  // Око Бестиария
    const [bx, bz] = LAYOUT.bases[team];
    return [
      { x: bx, z: bz, r: RULES.baseSight },
      ...this.heroes.filter(h => h.team === team && h.alive).map(h => ({ x: h.pos.x, z: h.pos.z, r: RULES.sight })),
      // Зоркий глаз: own magpies see over the trees.
      ...this.rawSummons.filter(s => s.team === team && s.kind === 'magpie').map(s => ({ x: s.pos.x, z: s.pos.z, r: PASSIVES.birdcatcher.sight, through: true })),
      ...this.reveals.map(r => ({ x: r.x, z: r.z, r: r.r, through: true })),
    ];
  }
}

/** `t` is this view's own local cosmetic clock at the instant `hs` describes: the wire sends
    `stunFor`/`respawnIn` as seconds remaining (clock-independent — see protocol.ts's HeroSnapshot header),
    and this converts each to a local absolute deadline, so the rest of the codebase (heroView.ts, hud.ts,
    fx-equivalent code) keeps comparing against `t` exactly like offline's real Hero fields always have.
    `h.pos`/`yaw`/`lift`/`state`/`swingAt`/`moving` are never touched here for ANY hero: the player's own
    come from reconcile() (predictedGame.ts's replay), every other hero's from interpolateOthers() (net/
    entityInterp.ts's buffer). Everything else (hp, alive, every status effect) is the SERVER's call and
    is applied from here: for the player the instant a snapshot lands, for every other hero from the same
    delayed frame its position is drawn from, so a death, a respawn or a stun shows where and when that
    hero is drawn — fog permitting: a hero a frame didn't see carries no such data, so its last-seen values
    stay frozen. */
export function applyHeroSnapshot(h: RemoteHero, hs: HeroSnapshot, t: number) {
  if (h.player) h.visible = true;         // always visible to self
  else if (!hs.visible) return;           // fog: nothing authoritative this tick, last-seen values freeze
  if (!hs.visible) return;                // type narrowing only — unreachable: the player's own entry
                                           // (handled above) is never the one the server fogs
  h.hp = hs.hp;
  h.maxHp = hs.maxHp;
  h.alive = hs.alive;
  h.stunUntil = hs.stunFor > 0 ? t + hs.stunFor : 0;
  h.respawnAt = hs.respawnIn !== undefined ? t + hs.respawnIn : 0;
  h.slowed = hs.slowed;
  h.shielded = hs.shielded;
  h.hasted = hs.hasted;
  h.enraged = hs.enraged;
  h.hasHot = hs.hasHot;
  h.empowered = hs.empowered;
  h.feintArmed = hs.feintArmed;
  h.invulnerable = hs.invulnerable;
  h.taunted = hs.taunted;
  h.pulled = hs.pulled;
  h.burning = !!hs.burning;  // `!!`: absent from a pre-protocol-6 snapshot
  h.dash = hs.dash ?? null;
  h.charge = hs.charge ?? null;
  h.hop = hs.hop ?? null;
  h.deadFor = hs.deadFor ?? null;
  h.artifacts = hs.artifacts;
  h.brandFor = hs.brandFor;
  h.cloaked = hs.cloaked;
  // Each absent from a pre-protocol-9 snapshot, and whenever it doesn't hold.
  h.invisible = hs.invisible === true;
  const f = hs.forced;
  h.forced = f && (f.kind === 'charm' || f.kind === 'fear') && Number.isFinite(f.left) && f.left > 0 ? { kind: f.kind, until: t + f.left } : null;
  h.confused = hs.confused === true;
  h.sleepless = hs.sleepless === true;
  h.silhouette = hs.silhouette === true;  // absent before protocol 17
  h.wolfUntil = typeof hs.wolfFor === 'number' && Number.isFinite(hs.wolfFor) && hs.wolfFor > 0 ? t + hs.wolfFor : 0;  // absent before protocol 18
  const to = hs.tether?.to;
  h.tethered = !!hs.tether;
  h.tetherTo = to && Number.isFinite(to.x) && Number.isFinite(to.z) ? { x: to.x, z: to.z } : null;
}
